import React, { useState, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const PROSPECTS_API = 'https://system.sales-management.cloud/api/prospects.php';

const statusOptions = [
    { value: 'new', label: '新規', color: 'bg-blue-100 text-blue-800' },
    { value: 'contacted', label: '接触済み', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'interested', label: '関心あり', color: 'bg-green-100 text-green-800' },
    { value: 'negotiating', label: '商談中', color: 'bg-purple-100 text-purple-800' },
    { value: 'converted', label: '成約', color: 'bg-emerald-100 text-emerald-800' },
    { value: 'lost', label: '失注', color: 'bg-red-100 text-red-800' }
];

const prospectOptions = [
    { value: '', label: '選択してください', color: 'bg-gray-100 text-gray-800' },
    { value: 'S', label: 'S:お客様から約束', color: 'bg-blue-100 text-blue-800' },
    { value: 'A', label: 'A:他社の営業歴あり', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'B', label: 'B:日時約束', color: 'bg-green-100 text-green-800' },
    { value: 'C', label: 'C:再訪予定', color: 'bg-purple-100 text-purple-800' },
];

function Prospects() {
    const getTodayDate = () => new Date().toISOString().split('T')[ 0 ];
    const getCurrentUserName = () => {
        try {
            const userInfoCookie = Cookies.get('user_info');
            if (!userInfoCookie) return '';
            const userInfo = JSON.parse(userInfoCookie);
            return userInfo?.name || '';
        } catch (err) {
            console.error('Failed to parse user info:', err);
            return '';
        }
    };

    const [ prospects, setProspects ] = useState([]);
    const [ loading, setLoading ] = useState(false);
    const [ error, setError ] = useState('');
    const [ success, setSuccess ] = useState('');
    const [ isModalOpen, setIsModalOpen ] = useState(false);
    const [ selectedProspect, setSelectedProspect ] = useState(null);
    const [ searchTerm, setSearchTerm ] = useState('');
    const [ filterStatus, setFilterStatus ] = useState('');

    const [ formData, setFormData ] = useState({
        registration_date: getTodayDate(),
        contact_person: getCurrentUserName(),
        customer_name: '',
        contact: '',
        status: 'new',
        prospect: '',
        notes: ''
    });

    // 見込み客一覧を取得
    const fetchProspects = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const token = Cookies.get('auth_token');
            if (!token) {
                setError('認証情報が見つかりません');
                return;
            }

            let url = PROSPECTS_API;
            const params = new URLSearchParams();
            if (searchTerm) params.append('search', searchTerm);
            if (filterStatus) params.append('status', filterStatus);
            if (params.toString()) url += `?${params.toString()}`;

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await response.json();
            

            if (data.status === 'success') {
                setProspects(data.data || []);
            } else {
                setError(data.message || '見込み客の取得に失敗しました');
            }
        } catch (err) {
            console.error('Fetch error:', err);
            setError('通信エラーが発生しました');
        } finally {
            setLoading(false);
        }
    }, [ searchTerm, filterStatus ]);

    useEffect(() => {
        fetchProspects();
    }, [ fetchProspects ]);

    // モーダルを開く
    const openModal = (prospect = null) => {
        if (prospect) {
            setSelectedProspect(prospect);
            setFormData({
                registration_date: prospect.registration_date || getTodayDate(),
                contact_person: prospect.contact_person || getCurrentUserName(),
                customer_name: prospect.customer_name || '',
                contact: prospect.contact || '',
                status: prospect.status || 'new',
                prospect: prospect.prospect || '',
                notes: prospect.notes || ''
            });
        } else {
            setSelectedProspect(null);
            setFormData({
                registration_date: getTodayDate(),
                contact_person: getCurrentUserName(),
                customer_name: '',
                contact: '',
                status: 'new',
                prospect: '',
                notes: ''
            });
        }
        setIsModalOpen(true);
        setError('');
        setSuccess('');
    };

    // モーダルを閉じる
    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedProspect(null);
    };

    // フォーム入力変更
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [ name ]: value }));
    };

    // 見込み客を保存
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        try {
            const token = Cookies.get('auth_token');
            if (!token) {
                setError('認証情報が見つかりません');
                return;
            }

            const method = selectedProspect ? 'PUT' : 'POST';
            const body = selectedProspect
                ? { ...formData, id: selectedProspect.id }
                : formData;

            const response = await fetch(PROSPECTS_API, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(body)
            });

            const data = await response.json();
            

            if (data.status === 'success') {
                setSuccess(selectedProspect ? '見込み客を更新しました' : '見込み客を登録しました');
                closeModal();
                fetchProspects();
            } else {
                setError(data.message || '保存に失敗しました');
            }
        } catch (err) {
            console.error('Save error:', err);
            setError('通信エラーが発生しました');
        }
    };

    // 見込み客を削除
    const handleDelete = async (id) => {
        if (!window.confirm('この見込み客を削除しますか？')) return;

        setError('');
        setSuccess('');

        try {
            const token = Cookies.get('auth_token');
            if (!token) {
                setError('認証情報が見つかりません');
                return;
            }

            const response = await fetch(PROSPECTS_API, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ id })
            });

            const data = await response.json();
            

            if (data.status === 'success') {
                setSuccess('見込み客を削除しました');
                fetchProspects();
            } else {
                setError(data.message || '削除に失敗しました');
            }
        } catch (err) {
            console.error('Delete error:', err);
            setError('通信エラーが発生しました');
        }
    };

    const getStatusLabel = (status) => {
        const option = statusOptions.find(opt => opt.value === status);
        return option || statusOptions[ 0 ];
    };

    return (
        <div className="max-w-7xl mx-auto p-4 md:p-6">
            <div className="mb-6">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">見込み客管理</h1>

                {/* 検索・フィルター */}
                <div className="flex flex-col md:flex-row gap-3 mb-4">
                    <input
                        type="text"
                        placeholder="顧客名・担当者名で検索..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                        <option value="">すべてのステータス</option>
                        {statusOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                    <button
                        onClick={() => openModal()}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                    >
                        <AddIcon /> 新規追加
                    </button>
                </div>

                {/* メッセージ表示 */}
                {error && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-sm text-red-600">{error}</p>
                    </div>
                )}
                {success && (
                    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
                        <p className="text-sm text-green-600">{success}</p>
                    </div>
                )}
            </div>

            {/* 見込み客リスト */}
            {loading ? (
                <div className="flex items-center justify-center py-20 text-gray-500">
                    読み込み中...
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        顧客名
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        担当者
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        連絡先
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        ステータス
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        備考
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        操作
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {prospects.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                                            見込み客が登録されていません
                                        </td>
                                    </tr>
                                ) : (
                                    prospects.map((prospect) => {
                                        const statusInfo = getStatusLabel(prospect.status);
                                        return (
                                            <tr key={prospect.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-medium text-gray-900">{prospect.customer_name}</div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900">{prospect.contact_person}</div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900">{prospect.contact}</div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${statusInfo.color}`}>
                                                        {statusInfo.label}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="text-sm text-gray-900 max-w-xs truncate">{prospect.notes || '-'}</div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <button
                                                        onClick={() => openModal(prospect)}
                                                        className="text-blue-600 hover:text-blue-900 mr-3"
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(prospect.id)}
                                                        className="text-red-600 hover:text-red-900"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* 登録・編集モーダル */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">
                                {selectedProspect ? '見込み客を編集' : '新規見込み客を登録'}
                            </h2>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            登録日
                                        </label>
                                        <input
                                            type="date"
                                            name="registration_date"
                                            value={formData.registration_date}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            readOnly
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            担当者
                                        </label>
                                        <input
                                            type="text"
                                            name="contact_person"
                                            value={formData.contact_person}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            readOnly
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            顧客名 <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="customer_name"
                                            value={formData.customer_name}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            連絡先
                                        </label>
                                        <input
                                            type="text"
                                            name="contact"
                                            value={formData.contact}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            ステータス <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            {statusOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            見込み <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            name="prospect"
                                            value={formData.prospect}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            {prospectOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        備考
                                    </label>
                                    <textarea
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleInputChange}
                                        rows="3"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                <div className="flex justify-end gap-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={closeModal}
                                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                        キャンセル
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                    >
                                        {selectedProspect ? '更新' : '登録'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Prospects;
